import { AlertTriangle, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import type { Discipline } from '@obraplan/core/domain/structure/discipline';
import type { JobRoleDiscipline } from '@obraplan/core/domain/workforce/job-role-discipline';
import type { JobRole } from '@obraplan/core/domain/workforce/team';
import { EmptyState } from '@/components/feedback/states';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/cn';

/**
 * Quem faz o quê — a matriz disciplina × função.
 *
 * É o cadastro que transforma o cronograma em proposta de alocação: sem ele, o sistema
 * sabe que a Alvenaria do 3º está em execução nesta semana e não tem como saber a quem
 * propô-la. Uma função sem nenhuma disciplina fica de fora da proposta automática, e a
 * tela diz isso na cara em vez de deixar o planejador descobrir pela ausência. É também o
 * que a importação da planilha consulta para ligar a mão de obra à função: só entre as
 * funções da disciplina do serviço.
 *
 * ## Por que uma matriz
 *
 * Era uma fileira de chips por função: vinte disciplinas repetidas em cada linha, as
 * marcadas misturadas às outras, e "quem executa Alvenaria?" exigia ler todas as fileiras.
 * Na matriz as duas perguntas são uma varredura: a linha diz quem executa a disciplina, a
 * coluna diz o que a função executa. As disciplinas vão nas linhas porque são mais, e
 * lista comprida rola para baixo; as funções são poucas e têm nome curto, cabem em
 * colunas.
 *
 * ## Em lote (14/10/2026)
 *
 * Caixa por caixa, ligar o Auxiliar de Obra às trinta disciplinas da fundação à cobertura
 * eram trinta cliques. Agora:
 *
 * - **arrastar** com o mouse marca o retângulo entre a primeira caixa e a última, com o
 *   contrário da primeira — uma coluna, uma linha ou um bloco de uma vez, como na planilha;
 * - o **nome da função** abre "todas as disciplinas", "nenhuma" e "copiar de" outra função;
 * - o **nome da disciplina** abre "todas as funções" e "nenhuma".
 *
 * O toque e o teclado continuam caixa por caixa: arrastar no celular é rolar a tela.
 *
 * **Só disciplinas ativas.** A inativa saiu da obra, e não há o que vincular a ela; os
 * vínculos que já existiam ficam guardados — toda mudança grava o conjunto inteiro da
 * função, com eles dentro —, e voltam a aparecer se a disciplina for reativada.
 *
 * Não há botão "salvar" porque não há rascunho: marcar é a decisão. Enquanto a lista não
 * volta do banco, a tela mostra o que foi marcado, para a caixa não piscar de volta.
 */
export interface RoleDisciplinesChange {
  readonly jobRoleId: string;
  /** O conjunto inteiro da função, com os vínculos das disciplinas inativas dentro. */
  readonly disciplineIds: readonly string[];
}

export interface RoleDisciplinesPanelProps {
  roles: readonly JobRole[];
  disciplines: readonly Discipline[];
  links: readonly JobRoleDiscipline[];
  /** Quantas pessoas ativas têm cada função — a função sem ninguém também merece aviso. */
  headcount?: ReadonlyMap<string, number>;
  canManage: boolean;
  /** Uma mudança por função alterada — um clique é uma, um lote são várias. */
  onChange: (changes: readonly RoleDisciplinesChange[]) => void;
}

function peopleLabel(people: number): string {
  if (people === 0) return 'ninguém';
  return `${String(people)} ${people === 1 ? 'pessoa' : 'pessoas'}`;
}

/**
 * O arrasto em curso: o estado que as caixas recebem e as duas pontas. Vale o retângulo entre
 * elas, como na planilha — o caminho do mouse pularia linhas quando ele corre.
 */
interface Pintura {
  readonly alvo: boolean;
  readonly de: Celula;
  readonly ate: Celula;
}

interface Celula {
  readonly roleId: string;
  readonly disciplineId: string;
}

const mesmoConjunto = (a: ReadonlySet<string>, b: ReadonlySet<string>): boolean =>
  a.size === b.size && [...a].every((id) => b.has(id));

export function RoleDisciplinesPanel({
  roles,
  disciplines,
  links,
  headcount,
  canManage,
  onChange,
}: RoleDisciplinesPanelProps) {
  const byRole = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const link of links) {
      const current = map.get(link.jobRoleId) ?? new Set<string>();
      current.add(link.disciplineId);
      map.set(link.jobRoleId, current);
    }
    return map;
  }, [links]);

  // O que já foi mandado gravar e ainda não voltou do banco. A lista nova o substitui.
  const [pendentes, setPendentes] = useState<ReadonlyMap<string, ReadonlySet<string>>>(
    () => new Map(),
  );
  useEffect(() => setPendentes(new Map()), [links]);

  const [pintura, setPinturaNaTela] = useState<Pintura | undefined>(undefined);
  // O soltar pode chegar antes do render da última caixa: ele lê a ref, que muda na hora.
  const pinturaAtual = useRef<Pintura | undefined>(undefined);
  const setPintura = (proxima: Pintura | undefined): void => {
    pinturaAtual.current = proxima;
    setPinturaNaTela(proxima);
  };
  /**
   * De onde veio a última ação na caixa: o clique do mouse já foi tratado pelo arrasto (de
   * uma caixa só, que seja); o teclado e o toque alternam pela própria caixa.
   */
  const ponteiro = useRef<string | undefined>(undefined);

  const activeDisciplines = useMemo(
    () =>
      disciplines
        .filter((discipline) => discipline.isActive)
        .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name)),
    [disciplines],
  );
  const activeRoles = useMemo(() => roles.filter((role) => role.isActive), [roles]);
  const hiddenDisciplines = disciplines.length - activeDisciplines.length;
  const activeIds = useMemo(
    () => new Set(activeDisciplines.map((discipline) => discipline.id as string)),
    [activeDisciplines],
  );
  const posicaoDaFuncao = useMemo(
    () => new Map(activeRoles.map((role, i) => [role.id as string, i])),
    [activeRoles],
  );
  const posicaoDaDisciplina = useMemo(
    () => new Map(activeDisciplines.map((discipline, i) => [discipline.id as string, i])),
    [activeDisciplines],
  );

  /** As caixas do retângulo do arrasto, função por função. */
  const doRetangulo = (p: Pintura): Celula[] => {
    const [r0, r1] = [posicaoDaFuncao.get(p.de.roleId), posicaoDaFuncao.get(p.ate.roleId)];
    const [d0, d1] = [
      posicaoDaDisciplina.get(p.de.disciplineId),
      posicaoDaDisciplina.get(p.ate.disciplineId),
    ];
    if (r0 === undefined || r1 === undefined || d0 === undefined || d1 === undefined) return [];
    const funcoes = activeRoles.slice(Math.min(r0, r1), Math.max(r0, r1) + 1);
    const daLinha = activeDisciplines.slice(Math.min(d0, d1), Math.max(d0, d1) + 1);
    return funcoes.flatMap((role) =>
      daLinha.map((discipline) => ({ roleId: role.id, disciplineId: discipline.id })),
    );
  };

  const vinculosDe = (roleId: string): ReadonlySet<string> =>
    pendentes.get(roleId) ?? byRole.get(roleId) ?? new Set<string>();

  /** Grava os conjuntos novos — só os que mudaram, numa chamada só. */
  const gravar = (novos: ReadonlyMap<string, ReadonlySet<string>>): void => {
    const mudancas: RoleDisciplinesChange[] = [];
    const proximos = new Map(pendentes);
    for (const [roleId, conjunto] of novos) {
      if (mesmoConjunto(conjunto, vinculosDe(roleId))) continue;
      proximos.set(roleId, conjunto);
      mudancas.push({ jobRoleId: roleId, disciplineIds: [...conjunto] });
    }
    if (mudancas.length === 0) return;
    setPendentes(proximos);
    onChange(mudancas);
  };

  /** A função com as disciplinas ativas trocadas por `ativas`; as inativas ficam. */
  const comAtivas = (roleId: string, ativas: Iterable<string>): Set<string> => {
    const conjunto = new Set([...vinculosDe(roleId)].filter((id) => !activeIds.has(id)));
    for (const id of ativas) conjunto.add(id);
    return conjunto;
  };

  // Soltar o botão em qualquer lugar — até fora da matriz — fecha o arrasto e grava.
  useEffect(() => {
    if (pintura === undefined) return;
    const soltar = (): void => {
      const ultima = pinturaAtual.current ?? pintura;
      const novos = new Map<string, Set<string>>();
      for (const { roleId, disciplineId } of doRetangulo(ultima)) {
        const conjunto = novos.get(roleId) ?? new Set(vinculosDe(roleId));
        if (ultima.alvo) conjunto.add(disciplineId);
        else conjunto.delete(disciplineId);
        novos.set(roleId, conjunto);
      }
      setPintura(undefined);
      gravar(novos);
    };
    const cancelar = (): void => setPintura(undefined);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', cancelar);
    return () => {
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', cancelar);
    };
    // `gravar` e `vinculosDe` leem o render corrente; o arrasto só precisa renovar o ouvinte
    // quando ele mesmo muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pintura === undefined]);

  if (activeDisciplines.length === 0) {
    return (
      <EmptyState
        title="Nenhuma disciplina ativa"
        description="Cadastre ou reative as disciplinas da obra em Configurações → Disciplinas para dizer qual função executa cada uma."
      />
    );
  }

  if (activeRoles.length === 0) {
    return (
      <EmptyState
        title="Nenhuma função"
        description="Cadastre as funções da obra — pedreiro, armador, eletricista — com o botão Nova função."
      />
    );
  }

  const linksOf = (roleId: string): number =>
    [...vinculosDe(roleId)].filter((id) => activeIds.has(id)).length;
  const totalLinks = activeRoles.reduce((sum, role) => sum + linksOf(role.id), 0);

  const noArrasto = (roleId: string, disciplineId: string): boolean => {
    if (pintura === undefined) return false;
    const r = posicaoDaFuncao.get(roleId) ?? -1;
    const d = posicaoDaDisciplina.get(disciplineId) ?? -1;
    const [r0 = -1, r1 = -1] = [
      posicaoDaFuncao.get(pintura.de.roleId),
      posicaoDaFuncao.get(pintura.ate.roleId),
    ];
    const [d0 = -1, d1 = -1] = [
      posicaoDaDisciplina.get(pintura.de.disciplineId),
      posicaoDaDisciplina.get(pintura.ate.disciplineId),
    ];
    return (
      r >= Math.min(r0, r1) &&
      r <= Math.max(r0, r1) &&
      d >= Math.min(d0, d1) &&
      d <= Math.max(d0, d1)
    );
  };

  const marcada = (roleId: string, disciplineId: string): boolean =>
    pintura !== undefined && noArrasto(roleId, disciplineId)
      ? pintura.alvo
      : vinculosDe(roleId).has(disciplineId);

  const alternar = (roleId: string, disciplineId: string): void => {
    const conjunto = new Set(vinculosDe(roleId));
    if (conjunto.has(disciplineId)) conjunto.delete(disciplineId);
    else conjunto.add(disciplineId);
    gravar(new Map([[roleId, conjunto]]));
  };

  const todasAsDisciplinas = (roleId: string, ligar: boolean): void =>
    gravar(new Map([[roleId, comAtivas(roleId, ligar ? activeIds : [])]]));

  const copiarDe = (roleId: string, origemId: string): void =>
    gravar(
      new Map([
        [
          roleId,
          comAtivas(
            roleId,
            [...vinculosDe(origemId)].filter((id) => activeIds.has(id)),
          ),
        ],
      ]),
    );

  const todasAsFuncoes = (disciplineId: string, ligar: boolean): void =>
    gravar(
      new Map(
        activeRoles.map((role) => {
          const conjunto = new Set(vinculosDe(role.id));
          if (ligar) conjunto.add(disciplineId);
          else conjunto.delete(disciplineId);
          return [role.id, conjunto] as const;
        }),
      ),
    );

  const comecarArrasto = (event: PointerEvent, roleId: string, disciplineId: string): void => {
    ponteiro.current = event.pointerType;
    if (!canManage || event.pointerType !== 'mouse' || event.button !== 0) return;
    // Sem isto o arrasto seleciona o texto dos cabeçalhos.
    event.preventDefault();
    const celula = { roleId, disciplineId };
    setPintura({ alvo: !vinculosDe(roleId).has(disciplineId), de: celula, ate: celula });
  };

  const passarPor = (roleId: string, disciplineId: string): void => {
    const atual = pinturaAtual.current;
    if (atual === undefined) return;
    if (atual.ate.roleId === roleId && atual.ate.disciplineId === disciplineId) return;
    setPintura({ ...atual, ate: { roleId, disciplineId } });
  };

  // As funções que já têm disciplina podem ser modelo de outra.
  const modelos = activeRoles.filter((role) => linksOf(role.id) > 0);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-(--color-content-muted)">
        {String(activeDisciplines.length)}{' '}
        {activeDisciplines.length === 1 ? 'disciplina' : 'disciplinas'} ×{' '}
        {String(activeRoles.length)} {activeRoles.length === 1 ? 'função' : 'funções'} ·{' '}
        {String(totalLinks)} {totalLinks === 1 ? 'vínculo' : 'vínculos'}
        {hiddenDisciplines > 0 &&
          ` · ${String(hiddenDisciplines)} ${hiddenDisciplines === 1 ? 'disciplina inativa fora da lista' : 'disciplinas inativas fora da lista'}`}
      </p>
      {canManage && (
        <p className="text-xs text-(--color-content-muted)">
          Arraste o mouse sobre as caixas para marcar várias de uma vez. O nome da função marca
          todas as disciplinas dela ou copia de outra função; o nome da disciplina marca todas as
          funções.
        </p>
      )}

      {/* A matriz rola na horizontal dentro da moldura; a coluna das disciplinas fica presa. */}
      <div
        className={cn(
          'overflow-x-auto rounded-(--radius-card) border border-(--color-border-subtle)',
          pintura !== undefined && 'select-none',
        )}
      >
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            Disciplinas executadas por cada função: cada linha é uma disciplina, cada coluna é uma
            função.
          </caption>
          <thead>
            <tr className="border-b border-(--color-border-subtle)">
              <th
                scope="col"
                className="sticky left-0 z-10 min-w-44 bg-(--color-surface-raised) px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)"
              >
                Disciplina
              </th>
              {activeRoles.map((role) => {
                // Função sem ninguém na obra também conta: o mapa só traz quem tem gente.
                const people = headcount === undefined ? undefined : (headcount.get(role.id) ?? 0);
                const semDisciplina = linksOf(role.id) === 0;
                const outros = modelos.filter((modelo) => modelo.id !== role.id);
                return (
                  <th
                    key={role.id}
                    scope="col"
                    // Colunas estreitas: catorze funções cabem no monitor com o menu recolhido.
                    className="min-w-18 px-1 py-2 text-center align-bottom text-xs font-medium"
                  >
                    <div className="flex flex-col items-center gap-0.5">
                      {canManage ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            aria-label={`Atalhos de ${role.name}`}
                            className="inline-flex items-center gap-0.5 rounded px-1 leading-tight hover:bg-(--color-surface-muted) focus-visible:outline-2 focus-visible:outline-(--color-brand)"
                          >
                            {role.name}
                            <ChevronDown
                              className="size-3 shrink-0 text-(--color-content-muted)"
                              aria-hidden="true"
                            />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center">
                            <DropdownMenuItem onSelect={() => todasAsDisciplinas(role.id, true)}>
                              Todas as disciplinas
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => todasAsDisciplinas(role.id, false)}>
                              Nenhuma disciplina
                            </DropdownMenuItem>
                            {outros.length > 0 && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>Copiar as disciplinas de</DropdownMenuLabel>
                                {outros.map((modelo) => (
                                  <DropdownMenuItem
                                    key={modelo.id}
                                    onSelect={() => copiarDe(role.id, modelo.id)}
                                  >
                                    {modelo.name}
                                    <span className="ml-auto text-xs text-(--color-content-muted)">
                                      {String(linksOf(modelo.id))}
                                    </span>
                                  </DropdownMenuItem>
                                ))}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="leading-tight">{role.name}</span>
                      )}
                      {people !== undefined && (
                        <span className="text-[11px] font-normal text-(--color-content-muted)">
                          {peopleLabel(people)}
                        </span>
                      )}
                      {semDisciplina && (
                        <span
                          className="flex items-center gap-1 text-[11px] font-normal text-(--color-state-warn)"
                          title="Sem disciplina, a função fica fora da proposta automática de alocação."
                        >
                          <AlertTriangle className="size-3" aria-hidden="true" />
                          sem vínculo
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {activeDisciplines.map((discipline) => {
              const executors = activeRoles.filter((role) =>
                vinculosDe(role.id).has(discipline.id),
              ).length;
              return (
                <tr
                  key={discipline.id}
                  className="group border-b border-(--color-border-subtle) last:border-0 hover:bg-(--color-surface-muted)"
                >
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-(--color-surface-raised) px-3 py-1 text-left font-normal group-hover:bg-(--color-surface-muted)"
                  >
                    <span className="flex items-center gap-2">
                      {/* O traço na cor da disciplina: o mesmo do corte da Estrutura. */}
                      <span
                        aria-hidden="true"
                        className="h-4 w-1 shrink-0 rounded-full"
                        style={{
                          backgroundColor: discipline.color ?? 'var(--color-border-subtle)',
                        }}
                      />
                      {canManage ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            aria-label={`Atalhos de ${discipline.name}`}
                            className="inline-flex items-center gap-0.5 rounded text-left font-medium hover:underline focus-visible:outline-2 focus-visible:outline-(--color-brand)"
                          >
                            {discipline.name}
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            <DropdownMenuItem onSelect={() => todasAsFuncoes(discipline.id, true)}>
                              Todas as funções executam
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => todasAsFuncoes(discipline.id, false)}>
                              Nenhuma função executa
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="font-medium">{discipline.name}</span>
                      )}
                      {executors === 0 && (
                        <span className="text-[11px] text-(--color-state-warn)">
                          ninguém executa
                        </span>
                      )}
                    </span>
                  </th>
                  {activeRoles.map((role) => {
                    const checked = marcada(role.id, discipline.id);
                    return (
                      <td key={role.id} className="px-1 py-1 text-center">
                        <label
                          className={cn(
                            'inline-flex size-9 items-center justify-center rounded-md md:size-7',
                            canManage
                              ? 'cursor-pointer hover:bg-(--color-surface-raised)'
                              : 'cursor-not-allowed',
                          )}
                          onPointerDown={(event) => comecarArrasto(event, role.id, discipline.id)}
                          onPointerEnter={() => passarPor(role.id, discipline.id)}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!canManage}
                            aria-label={`${role.name} executa ${discipline.name}`}
                            onKeyDown={() => {
                              ponteiro.current = 'teclado';
                            }}
                            onChange={() => {
                              if (ponteiro.current === 'mouse') return;
                              alternar(role.id, discipline.id);
                            }}
                            className="size-4 accent-(--color-brand) disabled:opacity-60"
                          />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
